import type { FieldUnitSpec, Measure } from "lab-testing-units";
import { action, computed, makeObservable, observable } from "mobx";
import {
  asDisplayUnits,
  type EquipmentUsedEntry,
  type LaboratoryInfo,
  type LabTestMetadata,
  type ProjectInfo,
  type SampleInfo,
  type StateTransitionContract,
  type TestRelationshipInput,
} from "@/contracts";
import { DisplayUnits } from "@/models/displayUnits/DisplayUnits";
import { describeSoil, soilDescriptionInputsFromSample } from "@/models/soilDescription";
import { LabTestStatus } from "../LabTestStatus";
import { SpecimenIdentityModel } from "./common/SpecimenIdentityModel";
import { withoutHiddenRows } from "./withoutHiddenRows";

export class LabTestDataModel {
  readonly id: string;
  readonly type: string;
  readonly method: string;
  readonly createdAt: string;
  readonly assignmentRemarks: string | null;
  readonly specimenIdentity: SpecimenIdentityModel;

  private readonly unitChoices: DisplayUnits;
  private readonly persistedSoilDescription: string | null;

  status: LabTestStatus;
  stateTransition: StateTransitionContract;

  processingTestId?: string | null;
  laboratory: LaboratoryInfo;
  sample?: SampleInfo;
  project?: ProjectInfo;

  constructor(data: LabTestMetadata) {
    this.unitChoices = new DisplayUnits(asDisplayUnits(data.displayUnits));
    this.id = data.id;
    this.type = data.type;
    this.method = data.method;
    this.createdAt = data.createdAt;
    this.assignmentRemarks = data.assignmentRemarks ?? null;
    this.specimenIdentity = new SpecimenIdentityModel(data);

    this.status = data.status;
    this.stateTransition = data.stateTransition ?? {};
    this.processingTestId = data.processingTestId;
    this.laboratory = data.laboratory;
    this.sample = data.sample;
    this.project = data.project;
    this.persistedSoilDescription = data.soilDescription ?? null;

    makeObservable(this, {
      status: observable,
      stateTransition: observable,
      laboratory: observable,
      sample: observable,
      project: observable,
      soilDescription: computed,
      setStatus: action,
      setSample: action,
    });
  }

  setStatus(status: LabTestStatus) {
    this.status = status;
  }

  setSample(sample: SampleInfo | undefined) {
    this.sample = sample;
  }

  get soilDescription(): string | null {
    if (this.status === LabTestStatus.ReadyForTesting || this.status === LabTestStatus.InTesting) {
      return describeSoil(soilDescriptionInputsFromSample(this.sample))?.description ?? null;
    }
    return this.persistedSoilDescription;
  }

  unitFor(key: string, spec: FieldUnitSpec, entered?: Measure | null): string {
    return this.unitChoices.unitFor(key, spec, entered);
  }

  setUnit(key: string, unitId: string): void {
    this.unitChoices.setUnit(key, unitId);
  }

  chosenUnitFor(key: string): string | undefined {
    return this.unitChoices.chosenFor(key);
  }

  dispose(): void {
    // Override in derived classes
  }

  toCompletionJSON(): LabTestMetadata {
    return withoutHiddenRows(this.toJSON()) as LabTestMetadata;
  }

  getCompletionMetadata(): object | undefined {
    return undefined;
  }

  getCompletionRelationships(): TestRelationshipInput[] | undefined {
    return undefined;
  }

  getEquipmentUsed(): EquipmentUsedEntry[] {
    return [];
  }

  toJSON(): LabTestMetadata {
    // getEquipmentUsed runs here on partial data via the auto-save reaction; a throwing override must not break saving.
    let equipmentUsed: EquipmentUsedEntry[] = [];
    try {
      equipmentUsed = this.getEquipmentUsed();
    } catch {
      equipmentUsed = [];
    }
    return {
      id: this.id,
      type: this.type,
      method: this.method,
      createdAt: this.createdAt,
      status: this.status,
      stateTransition: this.stateTransition,
      processingTestId: this.processingTestId,
      laboratory: this.laboratory,
      sample: this.sample,
      project: this.project,
      equipmentUsed,
      displayUnits: this.unitChoices.toJSON(),
      soilDescription: this.soilDescription,
    };
  }
}
